'use client';

import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Navbar } from '../../components/layout/navbar';
import { Footer } from '../../components/layout/footer';
import { ArticleCard } from '../../components/blog/article-card';
import { Button } from '../../components/ui/button';
import { useArticles } from '../../hooks/use-articles';
import { useTopics } from '../../hooks/use-topics';
import { useAuthStore } from '../../stores';
import { useGetUserById, useCheckUserLogin } from '../../hooks/use-user';
import {
  Search,
  X,
  Filter,
  Plus,
  BookOpen,
  TrendingUp,
  Atom,
  Sparkles,
  Grid,
  ChevronRight,
  LayoutDashboard,
  Menu,
  Hash,
  Activity,
} from 'lucide-react';
import { TopicList } from '../../components/blog/topic-list';
import { Pagination } from '../../components/ui/pagination';
import { Badge } from '../../components/ui/badge';

const ARTICLES_PER_PAGE = 12;

export default function BlogHomePage() {
  const { category } = useParams();
  const [selectedTopic, setSelectedTopic] = useState(category || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const articlesRef = useRef(null);
  const searchInputRef = useRef(null);

  const { articles, isArticlesLoading } = useArticles();
  const { data: availableTopics = [], isLoading: isTopicsLoading } =
    useTopics();

  const { walletAddress, isAuthenticated } = useAuthStore();
  const { data: user, isLoading: isLoadingUser } = useGetUserById(
    walletAddress
  );
  const { data: isLoggedIn } = useCheckUserLogin();

  // Count articles for each canonical topic
  const topicCounts = {};
  if (articles && Array.isArray(articles)) {
    articles.forEach((article) => {
      if (Array.isArray(article.topics)) {
        article.topics.forEach((topic) => {
          let topicNames = [];
          if (typeof topic === 'string') {
            topicNames = topic.split(',').map((t) => t.trim());
          } else if (topic?.slug || topic?.name) {
            topicNames = [(topic.slug || topic.name).trim()];
          }
          topicNames.forEach((name) => {
            if (name) {
              topicCounts[name] = (topicCounts[name] || 0) + 1;
            }
          });
        });
      }
    });
  }

  // Use canonical topics from useTopics
  const topics = availableTopics.map((topic) => ({
    name: topic.name,
    slug: topic.slug,
    count: topicCounts[topic.name] || 0,
  }));

  // Filter articles by topic if a topic is selected
  const displayedArticles = selectedTopic
    ? articles?.filter((article) => {
        if (!Array.isArray(article.topics)) return false;
        return article.topics.some((topic) => {
          let topicNames = [];
          if (typeof topic === 'string') {
            topicNames = topic.split(',').map((t) => t.trim());
          } else if (topic?.slug || topic?.name) {
            topicNames = [(topic.slug || topic.name).trim()];
          }
          return topicNames.includes(selectedTopic);
        });
      })
    : articles;

  // Sort articles by date (newest first)
  const sortedArticles = [...(displayedArticles || [])].sort((a, b) => {
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });

  // Update selected topic when category param changes
  useEffect(() => {
    if (category) {
      setSelectedTopic(category);
    }
  }, [category]);

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const query = searchQuery.toLowerCase();

    // Search based on how closely the article title matches the search query
    const results = articles
      ?.filter((article) => {
        const title = (article.title || '').toLowerCase();
        return title.includes(query);
      })
      .sort((a, b) => {
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();
        const indexA = titleA.indexOf(query);
        const indexB = titleB.indexOf(query);
        if (indexA === indexB) {
          return titleA.length - titleB.length;
        }
        return indexA - indexB;
      })
      .slice(0, 5);

    setSearchResults(results);
  }, [searchQuery, articles]);

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic || '');
    setSearchQuery('');
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    searchInputRef.current?.focus();
  };

  // Find the selected topic object for heading display
  const selectedTopicObj = topics.find(
    (topic) => topic.slug === selectedTopic || topic.name === selectedTopic
  );

  // Pagination calculations
  const totalPages = Math.ceil(
    (sortedArticles?.length || 0) / ARTICLES_PER_PAGE
  );
  const paginatedArticles =
    sortedArticles?.slice(
      (currentPage - 1) * ARTICLES_PER_PAGE,
      currentPage * ARTICLES_PER_PAGE
    ) || [];

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Sidebar Content Component
  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-3 text-white">
          <div className="p-2 bg-gradient-to-r from-violet-600 to-cyan-600 rounded-lg shadow-lg shadow-violet-500/20">
            <Atom className="h-5 w-5" />
          </div>
          <span className="font-bold text-lg tracking-tight">P-Articles</span>
        </div>
        {/* Close button for mobile */}
        <button
          onClick={() => setIsMobileMenuOpen(false)}
          className="lg:hidden p-2 text-zinc-400 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-8">
        {/* Stats Section */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-white mb-1">
              {articles?.length || 0}
            </div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
              Particles
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-white mb-1">
              {topics.length}
            </div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
              Topics
            </div>
          </div>
        </div>

        {/* Topics Section */}
        <div>
          <div className="flex items-center gap-2 text-zinc-400 mb-4 px-2">
            <Hash className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-wider">
              Knowledge Streams
            </span>
          </div>
          <TopicList
            topics={topics}
            selectedTopic={selectedTopic}
            onSelectTopic={(topic) => {
              handleTopicSelect(topic);
              setIsMobileMenuOpen(false);
            }}
            layout="vertical"
          />
        </div>

        {/* Live Activity (Mock) */}
        <div>
          <div className="flex items-center gap-2 text-zinc-400 mb-4 px-2">
            <Activity className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-wider">
              Live Activity
            </span>
          </div>
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="bg-zinc-900/30 border border-white/5 rounded-lg p-3 flex items-start gap-3"
              >
                <div className="h-2 w-2 mt-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                <div>
                  <p className="text-xs text-zinc-300 leading-relaxed">
                    New particle minted in{' '}
                    <span className="text-violet-400">Quantum Physics</span>
                  </p>
                  <p className="text-[10px] text-zinc-600 mt-1">2 mins ago</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer Links */}
      <div className="p-4 border-t border-white/5 text-xs text-zinc-600 text-center">
        © 2024 Talktrail P-Articles
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-violet-500/30 flex flex-col">
      <Navbar />

      <div className="flex flex-1 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
        </div>

        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-72 bg-black/40 backdrop-blur-xl border-r border-white/5 relative z-20 h-[calc(100vh-73px)] sticky top-[73px]">
          <SidebarContent />
        </aside>

        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-black border-r border-white/10 shadow-2xl animate-in slide-in-from-left duration-300">
              <SidebarContent />
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 relative z-10 h-[calc(100vh-73px)] overflow-y-auto custom-scrollbar">
          <div className="container mx-auto px-4 py-8 max-w-7xl">
            {/* Dashboard Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                  {selectedTopic ? (
                    <>
                      <span className="text-zinc-500 font-normal">Topic:</span>
                      <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                        {selectedTopicObj?.name || selectedTopic}
                      </span>
                    </>
                  ) : (
                    'Particle Dashboard'
                  )}
                </h1>
                <p className="text-zinc-400">
                  {selectedTopic
                    ? `Explore ${sortedArticles.length} particles in this stream`
                    : 'Your gateway to the decentralized knowledge universe'}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* Mobile Sidebar Trigger */}
                <Button
                  variant="outline"
                  size="icon"
                  className="lg:hidden border-white/10 bg-zinc-900/50 text-white"
                  onClick={() => setIsMobileMenuOpen(true)}
                >
                  <Menu className="h-5 w-5" />
                </Button>

                {user && user.isVerified && isLoggedIn ? (
                  <Button
                    asChild
                    className="bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white shadow-lg shadow-violet-500/20 border-0 rounded-xl h-12 px-6"
                  >
                    <Link to="/blog/create" className="flex items-center gap-2">
                      <Plus className="h-5 w-5" />
                      <span>Create Particle</span>
                    </Link>
                  </Button>
                ) : (
                  <Button
                    asChild
                    variant="outline"
                    className="border-violet-500/30 text-violet-400 hover:bg-violet-500/10 h-12 px-6 rounded-xl"
                  >
                    <Link to="/sign-in">Sign In to Create</Link>
                  </Button>
                )}
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative mb-10 group z-30">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 to-cyan-500/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-2 flex items-center shadow-lg">
                <Search className="h-5 w-5 text-zinc-500 ml-4" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for particles, topics, or authors..."
                  className="flex-1 bg-transparent border-none text-white placeholder:text-zinc-600 focus:ring-0 px-4 py-3 text-lg"
                />
                {searchQuery && (
                  <button
                    onClick={handleSearchClear}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors mr-2"
                  >
                    <X className="h-4 w-4 text-zinc-400" />
                  </button>
                )}
              </div>

              {/* Search Results Dropdown */}
              {isSearching && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-zinc-900/95 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl z-50">
                  <div className="p-4 bg-white/5 border-b border-white/5 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-violet-400" />
                    <span className="text-sm font-medium text-zinc-300">
                      Found {searchResults.length} matches
                    </span>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {searchResults.map((result) => (
                      <Link
                        key={result.oid}
                        to={`/blog/post/${result.oid}`}
                        className="flex items-center justify-between p-4 hover:bg-white/5 border-b border-white/5 last:border-0 transition-colors group"
                      >
                        <div>
                          <h4 className="font-medium text-white group-hover:text-violet-400 transition-colors">
                            {result.title}
                          </h4>
                          <p className="text-sm text-zinc-500 line-clamp-1">
                            {result.description || 'No description'}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-zinc-600 group-hover:text-violet-400" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Content Grid */}
            {isArticlesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-[400px] bg-zinc-900/50 border border-white/5 rounded-3xl animate-pulse"
                  />
                ))}
              </div>
            ) : sortedArticles?.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {paginatedArticles.map((article, index) => (
                    <div
                      key={article.oid}
                      className="group relative"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 to-cyan-500/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                      <div className="relative bg-zinc-900/40 backdrop-blur-sm border border-white/5 rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-300 hover:-translate-y-1 hover:border-violet-500/20 h-full">
                        <ArticleCard article={article} />
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mt-12 flex justify-center">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      showPageCount={true}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-white/10 rounded-3xl bg-white/5">
                <div className="h-20 w-20 rounded-full bg-zinc-900 flex items-center justify-center mb-6 border border-white/5">
                  <BookOpen className="h-10 w-10 text-zinc-600" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  No Particles Found
                </h3>
                <p className="text-zinc-400 max-w-md mb-8">
                  {selectedTopic
                    ? `No particles found in ${selectedTopic}. Be the first to contribute!`
                    : 'The universe is empty. Create the first particle of knowledge.'}
                </p>
                {user && user.isVerified && isLoggedIn && (
                  <Button
                    asChild
                    className="bg-zinc-100 text-black hover:bg-white rounded-xl px-8"
                  >
                    <Link to="/blog/create">Create Particle</Link>
                  </Button>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
